import {
	createContext,
	useContext,
	useMemo,
	useState,
	useCallback,
	useEffect,
} from "react";
import type {
	NavigationAufgabenstellungDTO,
	NavigationKlausurDTO,
	NavigationSachverhaltDTO,
} from "@/types/klausurEditor.types";

interface KlausurSidebarSelectionContextValue {
	navigationKlausur: NavigationKlausurDTO | null;
	setNavigationKlausur: (k: NavigationKlausurDTO | null) => void;
	selectedAufgabenstellungId: string | null;
	selectedSachverhaltId: string | null;
	setSelectedAufgabenstellung: (id: string | null) => void;
	setSelectedSachverhalt: (id: string | null) => void;
	expandedAufgabenstellungen: Set<string>;
	toggleAufgabenstellung: (id: string) => void;
	toggleAllAufgabenstellungen: () => void;
	getSelectedAufgabenstellung: () => NavigationAufgabenstellungDTO | null;
	getSelectedSachverhalt: () => NavigationSachverhaltDTO | null;
}

/**
 * Context for the sidebar selection of the klausur editor
 */
const KlausurSidebarSelectionContext = createContext<
	KlausurSidebarSelectionContextValue | undefined
>(undefined);

/**
 * Provider for the sidebar selection of the klausur editor
 */
export function KlausurSidebarSelectionProvider(props: {
	children: React.ReactNode;
	initialKlausur: NavigationKlausurDTO | null;
}) {
	const [navigationKlausur, _setNavigationKlausur] =
		useState<NavigationKlausurDTO | null>(props.initialKlausur);
	const [selectedAufgabenstellungId, setSelectedAufgabenstellungId] = useState<
		string | null
	>(null);
	const [selectedSachverhaltId, setSelectedSachverhaltId] = useState<
		string | null
	>(null);
	const [expandedAufgabenstellungen, setExpandedAufgabenstellungen] = useState<
		Set<string>
	>(new Set(props.initialKlausur?.aufgabenstellungen?.map((a) => a.id) ?? []));

	const setNavigationKlausur = useCallback((k: NavigationKlausurDTO | null) => {
		_setNavigationKlausur(k);
		if (k) {
			setExpandedAufgabenstellungen(
				new Set(k.aufgabenstellungen.map((a) => a.id)),
			);
		}
	}, []);

	/**
	 * Toggle the expansion state of an aufgabenstellung
	 */
	const toggleAufgabenstellung = useCallback((id: string) => {
		setExpandedAufgabenstellungen((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	/**
	 * Toggle the expansion state of all aufgabenstellungen
	 */
	const toggleAllAufgabenstellungen = useCallback(() => {
		if (!navigationKlausur) return;
		setExpandedAufgabenstellungen((prev) => {
			if (prev.size === navigationKlausur.aufgabenstellungen.length) {
				return new Set();
			}
			return new Set(navigationKlausur.aufgabenstellungen.map((a) => a.id));
		});
	}, [navigationKlausur]);

	// Ensure a default selection: first aufgabenstellung and its first sachverhalt
	useEffect(() => {
		if (!navigationKlausur) return;
		const firstAufgabe = navigationKlausur.aufgabenstellungen?.[0];
		if (!firstAufgabe) return;

		if (!selectedAufgabenstellungId) {
			setSelectedAufgabenstellungId(firstAufgabe.id);
		}

		const firstSachverhalt = firstAufgabe.sachverhalte?.[0];
		if (firstSachverhalt && !selectedSachverhaltId) {
			setSelectedSachverhaltId(firstSachverhalt.id);
		}
	}, [navigationKlausur, selectedAufgabenstellungId, selectedSachverhaltId]);

	const getSelectedAufgabenstellung = useCallback(() => {
		if (!navigationKlausur || !selectedAufgabenstellungId) return null;
		return (
			navigationKlausur.aufgabenstellungen.find(
				(a) => a.id === selectedAufgabenstellungId,
			) ?? null
		);
	}, [navigationKlausur, selectedAufgabenstellungId]);

	const getSelectedSachverhalt = useCallback(() => {
		const aufgabe = getSelectedAufgabenstellung();
		if (!aufgabe || !selectedSachverhaltId) return null;
		return (
			aufgabe.sachverhalte.find((s) => s.id === selectedSachverhaltId) ?? null
		);
	}, [getSelectedAufgabenstellung, selectedSachverhaltId]);

	/**
	 * Set the selected sachverhalt
	 */
	const setSelectedSachverhalt = useCallback(
		(id: string | null) => {
			console.log("KlausurSidebarSelectionContext.setSelectedSachverhalt", {
				previous: selectedSachverhaltId,
				next: id,
			});
			setSelectedSachverhaltId(id);
			if (id && navigationKlausur) {
				for (const a of navigationKlausur.aufgabenstellungen) {
					if (a.sachverhalte.some((s) => s.id === id)) {
						setSelectedAufgabenstellungId(a.id);
						setExpandedAufgabenstellungen((prev) => {
							const next = new Set(prev);
							next.add(a.id);
							return next;
						});
						break;
					}
				}
			}
		},
		[selectedSachverhaltId, navigationKlausur],
	);

	/**
	 * Set the selected aufgabenstellung and auto-select its first sachverhalt
	 */
	const setSelectedAufgabenstellung = useCallback(
		(id: string | null) => {
			setSelectedAufgabenstellungId(id);
			if (id) {
				setExpandedAufgabenstellungen((prev) => {
					const next = new Set(prev);
					next.add(id);
					return next;
				});

				// Auto-select the first sachverhalt of the selected aufgabenstellung
				if (navigationKlausur) {
					const aufgabe = navigationKlausur.aufgabenstellungen.find(
						(a) => a.id === id,
					);
					const firstSachverhalt = aufgabe?.sachverhalte?.[0];
					if (firstSachverhalt) {
						setSelectedSachverhaltId(firstSachverhalt.id);
					}
				}
			}
		},
		[navigationKlausur],
	);

	const value = useMemo(
		() => ({
			navigationKlausur,
			setNavigationKlausur,
			selectedAufgabenstellungId,
			selectedSachverhaltId,
			setSelectedAufgabenstellung,
			setSelectedSachverhalt,
			expandedAufgabenstellungen,
			toggleAufgabenstellung,
			toggleAllAufgabenstellungen,
			getSelectedAufgabenstellung,
			getSelectedSachverhalt,
		}),
		[
			expandedAufgabenstellungen,
			getSelectedAufgabenstellung,
			getSelectedSachverhalt,
			navigationKlausur,
			setNavigationKlausur,
			toggleAllAufgabenstellungen,
			toggleAufgabenstellung,
			selectedAufgabenstellungId,
			selectedSachverhaltId,
			setSelectedAufgabenstellung,
			setSelectedSachverhalt,
		],
	);

	return (
		<KlausurSidebarSelectionContext.Provider value={value}>
			{props.children}
		</KlausurSidebarSelectionContext.Provider>
	);
}

export function useKlausurSidebarSelection() {
	const ctx = useContext(KlausurSidebarSelectionContext);
	if (!ctx)
		throw new Error(
			"useKlausurSidebarSelection must be used within KlausurSidebarSelectionProvider",
		);
	return ctx;
}
